import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Paper,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useToast } from '../../providers/ToastContext';
import { apiService } from '../../services/api';
import {
    GetMerchantTransactionsRequest,
    MerchantTransaction,
    TransactionData,
} from '../../types';

const MerchantTransactionView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [transactions, setTransactions] = useState<MerchantTransaction[]>([]);
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { showToast } = useToast();
    const [selectedTransaction, setSelectedTransaction] =
        useState<TransactionData | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    const theme = useTheme();
    const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

    // Pagination state
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
        {
            page: 0,
            pageSize: 5,
        }
    );
    const fetchTransactions = async () => {
        if (!fromDate || !toDate) return;

        setLoading(true);
        const filterData: GetMerchantTransactionsRequest = {
            merchantId: Number(id),
            startDate: fromDate,
            endDate: toDate,
        };

        try {
            const response =
                await apiService.getMerchantTransactions(filterData);
            const processedTransactions = response.data.map(
                (tx: MerchantTransaction) => ({
                    ...tx,
                    payout1Amount: tx.payout1Amount ?? 0,
                    payout2Amount: tx.payout2Amount ?? 0,
                    formattedDate: new Date(tx.date).toLocaleDateString(),
                })
            );
            setTransactions(processedTransactions || []);
        } catch (err) {
            showToast('Failed to fetch transactions!', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, []);

    const handleEdit = async (id: number) => {
        if (!id) return;

        try {
            const apiRes = await apiService.getTransactionById(id);
            if (apiRes.data) {
                setSelectedTransaction(apiRes.data);
                setIsEditModalOpen(true);
                showToast(
                    'Transaction details ferched successfully',
                    'success'
                );
            } else {
                showToast('Transaction not found', 'error');
            }
        } catch (error) {
            console.error('Error fetching transaction:', error);

            showToast(
                'Unexpected error occurred while fetching transaction',
                'error'
            );
        }
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedTransaction(null);
    };

    const handleUpdateSuccess = () => {
        showToast('Transaction updated successfully!', 'success');
        setIsEditModalOpen(false);
        setSelectedTransaction(null);
        // Refresh charges data
        fetchTransactions();
    };

    const handleDeleteTransaction = async (transactionId: number) => {
        if (!transactionId) return;
        try {
            const response = await apiService.deleteTransaction(transactionId);
            if (response.msg === 'transaction deleted') {
                setTransactions(prev =>
                    prev.filter(tx => tx.id !== transactionId)
                );
                showToast('Transaction deleted successfully!', 'success');
            } else {
                showToast('Error deleting transaction!', 'error');
            }
        } catch (err) {
            showToast(
                'Unexpected error occurred while deleting transaction',
                'error'
            );
        }
    };

    const copyHeadersToClipboard = () => {
        const headers = columns
            .filter(col => col.field && col.field !== 'actions')
            .map(col => col.headerName)
            .join('\t');

        navigator.clipboard
            .writeText(headers)
            .then(() => showToast('Copied headers to clipboard!', 'success'))
            .catch(() => showToast('Failed to copy headers.', 'error'));
    };

    const columns: GridColDef[] = [
        // { field: 'id', headerName: 'ID', width: 70 },
        { field: 'day', headerName: 'Day', width: 100 },
        { field: 'formattedDate', headerName: 'Date', width: 120 },
        { field: 'sales', headerName: 'Sales', width: 100 },
        {
            field: 'chargebackCount',
            headerName: 'Chargeback Count',
            width: 150,
        },
        { field: 'chargebackFee', headerName: 'Chargeback Fee', width: 150 },
        {
            field: 'chargebackAmount',
            headerName: 'Chargeback Amount',
            width: 150,
        },
        { field: 'icAmount', headerName: 'IC Amount', width: 130 },
        { field: 'refundCount', headerName: 'Refund Count', width: 130 },
        {
            field: 'refundFeeAmount',
            headerName: 'Total Chargeback',
            width: 150,
        },
        {
            field: 'refundAmount',
            headerName: 'Total Refund Amount',
            width: 180,
        },
        { field: 'reserveCharge', headerName: 'Reserve Charge', width: 150 },
        { field: 'reserveFee', headerName: 'Reserve Fee', width: 130 },
        { field: 'wirecount', headerName: 'Wire Count', width: 120 },
        { field: 'wireAmount', headerName: 'Wire Amount', width: 120 },
        { field: 'achcount', headerName: 'ACH Count', width: 120 },
        { field: 'achAmount', headerName: 'ACH Amount', width: 130 },
        {
            field: 'payoutServiceCharge',
            headerName: 'Payout Service Charge',
            width: 180,
        },
        { field: 'totalCost', headerName: 'Total Cost', width: 130 },
        { field: 'paid_amount', headerName: 'Paid Amount', width: 130 },
        { field: 'netPayout', headerName: 'Net Payout', width: 130 },
        { field: 'transactions', headerName: 'Transactions Count', width: 180 },
        {
            field: 'transactionCharge',
            headerName: 'Total Processing Cost',
            width: 180,
        },
        { field: 'transactionFee', headerName: 'Transaction Fee', width: 150 },
        { field: 'payout1Amount', headerName: 'Payout 1 Amount', width: 150 },
        { field: 'payout2Amount', headerName: 'Payout 2 Amount', width: 150 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params: any) => (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                        pt: '10px',
                    }}
                >
                    <IconButton
                        aria-label="edit"
                        color="info"
                        size="small"
                        onClick={() => handleEdit(params.row.id)}
                    >
                        <EditIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                        aria-label="delete"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteTransaction(params.row.id)}
                    >
                        <DeleteIcon fontSize="inherit" />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ p: { xs: 2, sm: 2 } }}>
            {/* <Typography variant={isSmDown ? 'h6' : 'h5'} fontWeight="bold" mb={4}>
                Transactions View
            </Typography> */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 4 }}>
                <ReceiptLongIcon color="primary" fontSize="small" />
                <Typography variant={isSmDown ? 'h6' : 'h5'} fontWeight="bold">
                    Transactions View
                </Typography>
            </Box>
            {/* <Box
                sx={{
                    display: 'inline-flex', // only wraps content
                    alignItems: 'center',
                    backgroundColor: 'rgba(198, 82, 122, 0.49)',
                    gap: 1,
                    px: 2, // horizontal padding
                    py: 1, // vertical padding
                    borderRadius: 1, // rounded corners
                    mb: 4,
                }}
            >
                <ReceiptLongIcon color="primary" fontSize="small" />
                <Typography variant={isSmDown ? 'h6' : 'h5'} fontWeight="bold">
                    Transactions View
                </Typography>
            </Box> */}
            {/* Filters and Copy Headers Button */}
            <Grid container spacing={2} mb={3} alignItems="center">
                <Grid sx={{ xs: 12, sm: 5, md: 3 }}>
                    <TextField
                        fullWidth
                        label="From Date"
                        type="date"
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                        }}
                        value={fromDate}
                        onChange={e => setFromDate(e.target.value)}
                        size="small"
                    />
                </Grid>
                <Grid sx={{ xs: 12, sm: 5, md: 3 }}>
                    <TextField
                        fullWidth
                        label="To Date"
                        type="date"
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                        }}
                        value={toDate}
                        onChange={e => setToDate(e.target.value)}
                        size="small"
                    />
                </Grid>
                <Grid sx={{ xs: 12, sm: 2, md: 2 }}>
                    <Button
                        variant="contained"
                        onClick={fetchTransactions}
                        fullWidth
                        sx={{ height: '40px' }}
                    >
                        Find
                    </Button>
                </Grid>
                <Grid sx={{ xs: 12, sm: 2, md: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={copyHeadersToClipboard}
                        fullWidth
                        sx={{
                            height: '40px',
                            backgroundColor: 'rgba(135, 206, 250, 0.2)',
                            borderColor: 'rgba(21, 60, 84, 0.45)',
                            color: '#1976d2',
                            '&:hover': {
                                backgroundColor: 'rgba(135, 206, 250, 0.3)',
                                borderColor: 'rgba(135, 206, 250, 0.7)',
                            },
                        }}
                        startIcon={<ContentCopyIcon />}
                    >
                        Copy Headers
                    </Button>
                </Grid>
            </Grid>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper elevation={3} sx={{ borderRadius: 2 }}>
                    <DataGrid
                        rows={transactions}
                        columns={columns}
                        getRowId={row => row.id}
                        autoHeight
                        pageSizeOptions={[5, 10, 20]}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        checkboxSelection
                        disableRowSelectionOnClick={false}
                        sx={{
                            flexGrow: 1,
                            '& .MuiDataGrid-columnHeaders': {
                                backgroundColor: theme.palette.primary.main,
                                minHeight: '56px',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                color: '#000',
                                fontSize: '14px',
                                visibility: 'visible',
                                display: 'block',
                                fontWeight: 'bold',
                            },
                        }}
                    />
                </Paper>
            )}

            {/* <ChargeModal
                open={isEditModalOpen}
                onClose={handleCloseEditModal}
                title="Edit Charge"
            >
                <MerchantTransactions
                    mode="edit"
                    transactionData={selectedTransaction}
                    onSuccess={handleUpdateSuccess}
                    onCancel={handleCloseEditModal}
                    isModal={true}
                />
            </ChargeModal> */}
        </Box>
    );
};

export default MerchantTransactionView;
